import { h } from "preact"
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "preact/hooks"
import fetch from "unfetch"
import Card from "../components/Card"
import Layout from "../components/Layout"
import EyeIcon from "../components/EyeIcon"
import LockIcon from "../components/LockIcon"
import MobileIcon from "../components/MobileIcon"
const endpoint = "/api/v4/auth"

export default function Login() {
    try {
        const query = useQuery()

        if (query.has("logout-successful")) {
            return <LogoutSuccessful />
        }

        const returnUrl = query.get("r")
        if (!returnUrl) throw new Error("Der Parameter `r` fehlt.")

        const version = query.get("v") || "1"
        const [{ phase, data }, setData] = useState({ phase: "username", data: { version, returnUrl } })

        switch (phase) {
            case "username":
                return <UsernamePassword data={data} setData={setData} />
            case "factor":
                return <Factor data={data} setData={setData} />
            case "selfservice":
                return <Selfservice data={data} />
            case "email-otp":
                return <EmailOTP data={data} setData={setData} />
            case "return":
                return <Return data={data} />
        }
    } catch (error) {
        return <Fehler error={error} />
    }
}

function EmailOTP({ data, setData }) {
    const input = useRef()
    const [emailotpCode, setEmailotpCode] = useState("")
    const [{ submitting, error, submitted }, setState] = useState({
        submitting: false,
        error: null,
        submitted: false,
    })

    const onSubmitOTP = async (e) => {
        if (e) e.preventDefault()

        // Don't submit if already submitting or code is empty
        if (submitting || emailotpCode.length < 1) return

        setState((prev) => ({ ...prev, submitting: true }))

        try {
            const form = new URLSearchParams()
            form.append("username", data.username)
            form.append("emailOtpEingabe", emailotpCode)
            form.append("completionToken", data.completionToken)

            const response = await fetch(`${endpoint}/authenticate-email-otp`, { method: "POST", body: form })
            if (response.ok) {
                const { nextStep, ...responseData } = await handleResponse(response)
                setState((prev) => ({ ...prev, submitted: true }))
                setData({
                    phase: nextStep,
                    data: {
                        ...data,
                        ...responseData,
                    },
                })
            } else {
                const resp = await response.json()
                setState((prev) => ({
                    ...prev,
                    error: { message: resp.fehler },
                    submitting: false,
                }))
            }
        } catch (err) {
            setState((prev) => ({
                ...prev,
                error: err,
                submitting: false,
            }))
        }
    }

    useLayoutEffect(() => {
        input.current.focus()
    }, [])

    // Handle OTP token input changes
    const handleCodeChange = (e) => {
        const value = e.target.value
        if (value === "" || (/^[0-9A-Za-z]+$/.test(value))) {
            setEmailotpCode(value)
        }
    }

    // Function to request a new code
    const requestNewCode = async () => {
        try {
            const form = new URLSearchParams()
            form.append("completionToken", data.completionToken)
            const response = await fetch(`${endpoint}/send-new-email-otp`, { method: "POST", body: form })
            if (response.ok) {
                const { nextStep, ...responseData } = await handleResponse(response)
                setData({
                    phase: nextStep,
                    data: {
                        ...data,
                        ...responseData,
                        faktor: responseData.faktoren.length ? responseData.faktoren[0] : null,
                    },
                })
            } else {
                const resp = await response.json()
                setState({ error: { message: resp.fehler }, submitted: false })
            }
        } catch (err) {
            setState({ error: err, submitted: true })
        }
    }

    return (
        <Layout>
            <Card title="Bestätigungscode eingeben">
                <div className="p-3">
                    <div className={""}>
                        <p>Wir haben Ihnen einen Bestätigungscode an die in Ihrem Benutzerkonto hinterlegte E-Mail-Adresse gesendet.</p>
                        <p>Bitte geben Sie den Code unten ein, um Ihre Anmeldung zu bestätigen.</p>
                        <p>Der Code ist für {data.params.haltbarkeitInMinuten || "5"} Minuten gültig.</p>
                        <p>
                            Falls Sie keinen Code erhalten haben, prüfen Sie bitte Ihren Spam-Ordner oder fordern Sie einen neuen Code an:{" "}
                            <a
                                href="#"
                                style={{ color: "green" }}
                                onClick={(e) => {
                                    e.preventDefault()
                                    requestNewCode()
                                }}
                            >
                                Erneut senden
                            </a>
                        </p>
                    </div>

                    <form onSubmit={onSubmitOTP} className={`${submitted ? "was-validated" : ""}`} novalidate>
                        {error && <div className="alert alert-danger fs-5">{error.message}</div>}
                        <div className="form-group mb-4">
                            <label htmlFor="EmailOTP" className="fs-4 mb-2">
                                Bestätigungscode
                            </label>
                            <input
                                id="EmailOTP"
                                name="EmailOTP"
                                placeholder="Bestätigungscode"
                                required
                                className="form-control fs-3 py-3"
                                value={emailotpCode}
                                onChange={handleCodeChange}
                                disabled={submitting || submitted}
                                ref={input}
                                autoComplete="one-time-code"
                            />
                            <div className="invalid-feedback fs-5">Bitte geben Sie den OTP-Code ein.</div>
                        </div>

                        <div className="d-flex justify-content-end align-items-baseline">
                            <button type="submit" className="btn btn-primary" disabled={submitting || emailotpCode.length < 1}>
                                Weiter
                            </button>
                        </div>
                    </form>
                </div>
            </Card>
        </Layout>
    )
}

function UsernamePassword({ data, setData }) {
    const input = useRef()
    const [username, setUsername] = useState("")
    const [password, setPassword] = useState("")
    const [showPassword, setShowPassword] = useState(false)
    const [{ submitting, error, submitted }, setState] = useState({})

    const onSubmit = useSubmit(data, setData, username, password, setState)

    useLayoutEffect(() => {
        input.current.focus()
    }, [])

    return (
        <Layout>
            <Card title="Bitte melden Sie sich an.">
                <form onSubmit={onSubmit} className={`${submitted ? "was-validated" : ""}`} noValidate>
                    {error && <div className="alert alert-danger">{error.message}</div>}
                    <div className="form-group">
                        <label htmlFor="username">Benutzername</label>
                        <input
                            id="username"
                            name="username"
                            required
                            autoComplete="username"
                            className="form-control"
                            value={username}
                            onChange={(e) => setUsername(e.currentTarget.value)}
                            disabled={submitting}
                            ref={input}
                        />
                        <div class="invalid-feedback">Pflichtfeld</div>
                    </div>
                    <div className="form-group">
                        <label htmlFor="password">Kennwort</label>
                        <div className="input-group">
                            <input
                                id="password"
                                name="password"
                                type={showPassword ? "text" : "password"}
                                required
                                autoComplete="current-password"
                                className="form-control"
                                value={password}
                                onChange={(e) => setPassword(e.currentTarget.value)}
                                disabled={submitting}
                            />
                            <div className="input-group-append">
                                <button
                                    type="button"
                                    className="btn btn-outline-primary"
                                    onClick={() => setShowPassword((show) => !show)}
                                    disabled={submitting}
                                    aria-label={showPassword ? "Kennwort verbergen" : "Kennwort anzeigen"}
                                    aria-pressed={showPassword}
                                    aria-controls="password"
                                    title={showPassword ? "Kennwort verbergen" : "Kennwort anzeigen"}
                                >
                                    <EyeIcon slashed={showPassword} />
                                </button>
                            </div>
                        </div>
                        {submitted && !password && <div className="invalid-feedback d-block">Pflichtfeld</div>}
                    </div>
                    <div className="d-flex justify-content-end align-items-baseline">
                        <button type="submit" className="btn btn-primary" disabled={submitting}>
                            Weiter
                            {submitting && <span className="spinner-border spinner-border-sm ml-2" role="status" aria-hidden="true" />}
                        </button>
                    </div>
                </form>
            </Card>
        </Layout>
    )
}

const useSubmit = (data, setData, username, password, setState) => {
    return useCallback(
        async (e) => {
            e.preventDefault()

            if (username.trim().length && password.trim().length) {
                setState({ submitting: true, submitted: false })

                try {
                    const form = new URLSearchParams()
                    form.append("username", username)
                    form.append("password", password)
                    form.append("returnUrl", data.returnUrl)
                    const response = await fetch(`${endpoint}/authenticate`, { method: "POST", body: form })
                    if (response.ok) {
                        const { nextStep, ...responseData } = await handleResponse(response)
                        setData({
                            phase: nextStep,
                            data: {
                                ...data,
                                ...responseData,
                                faktor: responseData.faktoren.length ? responseData.faktoren[0] : null,
                                username,
                                password,
                            },
                        })
                    } else {
                        const resp = await response.json()
                        setState({ error: { message: resp.fehler }, submitted: false })
                    }
                } catch (err) {
                    setState({ error: err, submitted: true })
                }
            } else {
                setState({ submitted: true })
            }
        },
        [data, setData, username, password, setState]
    )
}

function Factor({ data, setData }) {
    const [{ submitting, error }, setState] = useState({})
    const [code, setCode] = useState("")

    const onSubmit = useCallback(
        async (e) => {
            e.preventDefault()
            setState({ submitting: true })

            try {
                const form = new URLSearchParams()
                form.append("username", data.username)
                form.append("password", data.password)
                form.append("factorId", data.faktor.id)
                form.append("factorCode", code)
                form.append("returnUrl", data.returnUrl)
                const response = await fetch(`${endpoint}/authorize`, { method: "POST", body: form })
                if (response.ok) {
                    const { nextStep, ...responseData } = await handleResponse(response)
                    setData({
                        phase: nextStep,
                        data: {
                            ...data,
                            ...responseData,
                        },
                    })
                } else {
                    const resp = await response.json()
                    setState({ error: { message: resp.fehler } })
                }
            } catch (err) {
                setState({ error: err })
            }
        },
        [data.faktor, code]
    )

    const setFaktor = useCallback((faktor) => {
        setData((p) => ({ ...p, data: { ...p.data, faktor, error: undefined } }))
    }, [])

    return (
        <Layout>
            <Card title="Bestätigung in zwei Schritten">
                <p>Mit diesem zusätzlichen Schritt bestätigen Sie Ihre Identität bei der Anmeldung.</p>
                {data.faktor ? (
                    <form noValidate onSubmit={onSubmit}>
                        {error && <div className="alert alert-danger">{error.message}</div>}
                        <FactorForm faktor={data.faktor} disabled={submitting} code={code} setCode={setCode} />
                        <div className="d-flex flex-row-reverse justify-content-between align-items-baseline">
                            <button type="submit" className="btn btn-primary" disabled={submitting}>
                                Weiter
                                {submitting && <span className="spinner-border spinner-border-sm ml-2" role="status" aria-hidden="true" />}
                            </button>
                            {data.faktoren.length > 1 && (
                                <button type="reset" className="btn btn-link" onClick={() => setFaktor(null)}>
                                    Andere Option wählen
                                </button>
                            )}
                        </div>
                    </form>
                ) : (
                    <FactorSelect faktoren={data.faktoren} setFaktor={setFaktor} />
                )}
            </Card>
        </Layout>
    )
}

function FactorSelect({ faktoren, setFaktor }) {
    return (
        <div className="list-group">
            {faktoren.map((faktor) => (
                <button
                    type="button"
                    key={faktor.id}
                    className="list-group-item list-group-item-action d-flex align-items-start"
                    onClick={() => setFaktor(faktor)}
                >
                    <FaktorOption {...faktor} />
                </button>
            ))}
        </div>
    )
}

function FaktorOption({ type, label }) {
    switch (type) {
        case "totp":
            return (
                <>
                    <MobileIcon className="mr-3" />
                    <span>
                        Bestätigungscode über die App <strong>{label}</strong> anfordern.
                    </span>
                </>
            )
        case "backup":
            return (
                <>
                    <LockIcon className="mr-3" />
                    <span>Einen der Backup-Codes eingeben.</span>
                </>
            )
    }
}

function FactorForm(props) {
    switch (props.faktor.type) {
        case "totp":
            return <TOTPFactor {...props} />
        case "backup":
            return <BackupFactor {...props} />
        default:
            return (
                <div className="alert alert-danger">
                    Unbekannter Authentifizierungs-Faktor: <code>{props.faktor.type}</code>
                </div>
            )
    }
}

function TOTPFactor({ faktor, disabled, code, setCode }) {
    return (
        <div className="form-group">
            <label htmlFor="code">
                Bestätigungscode über <strong>{faktor.label}</strong> eingeben
            </label>
            <input
                id="code"
                name="code"
                autoFocus
                required
                inputMode="numeric"
                pattern="[0-9]+"
                autoComplete="one-time-code"
                className="form-control"
                value={code}
                onChange={(e) => setCode(e.currentTarget.value)}
                disabled={disabled}
            />
        </div>
    )
}

function BackupFactor({ disabled, code, setCode }) {
    return (
        <div className="form-group">
            <label htmlFor="code">Geben Sie einen Ihrer achtstelligen Backup-Codes ein.</label>
            <input
                id="code"
                name="code"
                autoFocus
                required
                inputMode="numeric"
                pattern="[0-9]+"
                autoComplete="off"
                className="form-control"
                value={code}
                onChange={(e) => setCode(e.currentTarget.value)}
                disabled={disabled}
            />
        </div>
    )
}

async function handleResponse(response) {
    if (!response.ok) {
        let error
        try {
            error = new Error((await response.json()).message)
        } catch (e) {
            console.error("Error parsing error response:", e)
            error = new Error(`Fehler: ${response.status}`)
        }
        throw error
    }

    return response.json()
}

function Selfservice({
    data: {
        redirect: { method, url, params },
    },
}) {
    return (
        <Layout>
            <Card title="Weiterleitung">
                <Redirect method={method || "POST"} url={url} params={{ ...params, r: window.location.href }}>
                    Ihr Benutzerkonto ist noch nicht vollständig. Sie werden zum Selfservice weitergeleitet&hellip;
                </Redirect>
            </Card>
        </Layout>
    )
}

function Return({ data: { version, redirect } }) {
    return (
        <Layout>
            <Card title="Weiterleitung">
                <Redirect {...redirect} method={version === "1" ? "GET" : "POST"}>
                    Sie werden zum Fachverfahren weitergeleitet&hellip;
                </Redirect>
            </Card>
        </Layout>
    )
}

function Redirect({ method, url, params, children }) {
    const ref = useRef()
    useEffect(() => {
        ref.current.submit()
    }, [])

    return (
        <>
            <div className="d-flex align-items-center">
                <div>{children}</div>
                <span className="spinner-border text-secondary mr-3" />
            </div>
            <form action={url} method={method} ref={ref}>
                {Object.keys(params).map((name) => (
                    <input key={name} type="hidden" name={name} value={params[name]} />
                ))}
            </form>
        </>
    )
}

function Fehler({ error: { message } }) {
    return (
        <Layout>
            <Card title="Fehler">
                <div className="alert alert-danger">{message}</div>
            </Card>
        </Layout>
    )
}

function LogoutSuccessful() {
    return (
        <Layout>
            <Card title="Erfolgreich abgemeldet">
                <div className="alert alert-success">
                    Sie haben sich erfolgreich abgemeldet. Bitte schließen Sie als zusätzliche Sicherheitsmaßnahme dieses Browserfenster.
                </div>
            </Card>
        </Layout>
    )
}

function useQuery() {
    return new URLSearchParams(window.location.search)
}
